import TranslationService from "../../services/TranslationService";
import Vue from "vue";
import { mapState } from "vuex";
import { isDefined } from "../../helper/utils";
import { isPhoneRequired, hasPhoneNumber } from "../../helper/phoneRequired";
const NotificationService = require("../../services/NotificationService");

export default Vue.component("shipping-profile-select", {

    props:
    {
        template:
        {
            type: String,
            default: "#vue-shipping-profile-select"
        },
        paddingClasses:
        {
            type: String,
            default: null
        },
        paddingInlineStyles:
        {
            type: String,
            default: null
        },
        paymentContainerIsOverwritten:
        {
            type: Boolean,
            default: false
        }
    },

    data()
    {
        return {
            promptedPhoneKeys: []
        };
    },

    computed: mapState({
        shippingProfileList: state => state.checkout.shipping.shippingProfileList,
        maxDeliveryDays: state => state.checkout.shipping.maxDeliveryDays,
        shippingProfileId: state => state.checkout.shipping.shippingProfileId,
        showError: state => state.checkout.validation.shippingProfile.showError,
        isBasketLoading: state => state.basket.isBasketLoading,
        isCheckoutReadonly: state => state.checkout.readOnly,
        selectedPaymentMethodId: state => state.checkout.payment.methodOfPaymentId,
        billingAddress: state => state.address.billingAddress,
        deliveryAddress: state => state.address.deliveryAddress
    }),

    /**
     * Add a shipping provider
     * Initialise the event listener
     */
    created()
    {
        this.$store.commit("setShippingProfileValidator", this.validate);
        this.$store.commit("setPhoneNumberValidator", this.validatePhoneNumber);
    },

    methods:
    {
        /**
         * Method on shipping profile changed
         */
        onShippingProfileChange(shippingProfileId)
        {
            this.$store.dispatch("selectShippingProfile", this.shippingProfileList.find(shippingProfile => shippingProfile.parcelServicePresetId === shippingProfileId))
                .then(data =>
                {
                    document.dispatchEvent(new CustomEvent("afterShippingProfileChanged", { detail: this.shippingProfileId }));
                },
                error =>
                {
                    console.log("error");
                });

            this.validate();
        },

        /**
         * Abholung/Express: Telefonnummer an der Rechnungsadresse, bei Express auch an
         * einer abweichenden Lieferadresse (-99 = wie Rechnungsadresse).
         * @returns {object|null} { addressType, address } der ersten Adresse ohne Nummer
         */
        getAddressMissingPhone()
        {
            // Ohne Rechnungsadresse blockiert bereits der invoiceAddress-Validator
            if (!this.billingAddress)
            {
                return null;
            }

            if (isPhoneRequired(this.shippingProfileId, "1") && !hasPhoneNumber(this.billingAddress))
            {
                return { addressType: "1", address: this.billingAddress };
            }

            const hasSeparateDeliveryAddress = this.deliveryAddress && Number(this.deliveryAddress.id) !== -99;

            if (hasSeparateDeliveryAddress && isPhoneRequired(this.shippingProfileId, "2") && !hasPhoneNumber(this.deliveryAddress))
            {
                return { addressType: "2", address: this.deliveryAddress };
            }

            return null;
        },

        /**
         * Bearbeiten-Modal der betroffenen Adresse oeffnen, dort ist Telefon dann Pflichtfeld.
         * Rechnungsadresse wie beim Rechnungskauf (bigtex/PaymentProviderSelect.js),
         * Lieferadresse ueber den Listener in customer/AddressSelect/AddressSelect.js.
         */
        promptPhoneNumber({ addressType, address })
        {
            if (addressType === "1")
            {
                vueEventHub.$emit("changedToInvoice", address);
                NotificationService.error(
                    TranslationService.translate("Bigtex5::Template.checkoutPhoneRequiredBilling")
                );
            }
            else
            {
                vueEventHub.$emit("bigtex.phoneRequired.delivery", address);
                NotificationService.error(
                    TranslationService.translate("Bigtex5::Template.checkoutPhoneRequiredDelivery")
                );
            }
        },

        /**
         * Registriert als phoneNumber-Validator, aufgerufen von PlaceOrder.
         */
        validatePhoneNumber()
        {
            const missing = this.getAddressMissingPhone();

            this.$store.commit("setPhoneNumberShowError", !!missing);

            if (missing)
            {
                this.promptPhoneNumber(missing);
            }
        },

        /**
         * Egal in welcher Reihenfolge Adresse und Versandart gewaehlt werden: sobald die
         * Kombination eine Nummer verlangt und sie fehlt, einmal das Modal oeffnen.
         * Pro Versandart + Adresse nur einmal, damit das Modal nach dem Schliessen nicht
         * sofort wiederkommt - beim Bestellen greift validatePhoneNumber ohnehin erneut.
         */
        checkPhoneNumber()
        {
            const missing = this.getAddressMissingPhone();

            if (!missing || this.isCheckoutReadonly)
            {
                return;
            }

            const promptKey = `${this.shippingProfileId}:${missing.addressType}:${missing.address.id}`;

            if (this.promptedPhoneKeys.includes(promptKey))
            {
                return;
            }

            this.promptedPhoneKeys.push(promptKey);
            this.promptPhoneNumber(missing);
        },

        validate()
        {
            const showError = this.shippingProfileId <= 0 || this.shippingProfileList.length <= 0;

            this.$store.commit("setShippingProfileShowError", showError);

            if (showError)
            {
                NotificationService.error(
                    TranslationService.translate("Ceres::Template.checkoutCheckShippingProfile")
                );
            }
        },

        getTooltip(shippingProfileId, methodOfPaymentId)
        {
            let translationKey = "";

            const params = {};

            for (let i = 0; i < this.shippingProfileList.length; i++)
            {
                const shippingProfile = this.shippingProfileList[i];

                if (shippingProfile.parcelServicePresetId === shippingProfileId)
                {
                    if (this.paymentContainerIsOverwritten)
                    {
                        translationKey = "Ceres::Template.checkoutChangePaymentMethodToHint";
                        params.paymentMethodNames = shippingProfile.allowedPaymentMethodNames.join(",");
                    }
                    else
                    {
                        translationKey = "Ceres::Template.checkoutChangePaymentMethodHint";
                    }
                    break;
                }
            }

            return TranslationService.translate(translationKey, params);
        },

        isPaymentMethodExcluded(shippingProfile, selectedPaymentMethodId)
        {
            if (isDefined(shippingProfile.excludedPaymentMethodIds))
            {
                return shippingProfile.excludedPaymentMethodIds.includes(selectedPaymentMethodId);
            }
            else
            {
                return false;
            }
        },

        /**
         * Enter auf dem Listeneintrag wie einen Klick behandeln (Tastaturbedienung).
         * Ceres ruft das per @keydown in seinem eigenen Twig auf. Unser Theme-Twig
         * nutzt es aktuell nicht - welches Template gerendert wird, haengt an der
         * Backend-Konfiguration, deshalb steht die Methode hier vorsorglich.
         */
        handleKeydown(event, inputId)
        {
            if (event.key === "Enter" || event.keyCode === 13)
            {
                event.preventDefault();

                const input = document.getElementById(inputId);

                if (input)
                {
                    input.click();
                }
            }
        }
    },

    watch:
    {
        shippingProfileId()
        {
            this.checkPhoneNumber();
        },

        billingAddress()
        {
            this.checkPhoneNumber();
        },

        deliveryAddress()
        {
            this.checkPhoneNumber();
        }
    }
});
